import {
  ArrowDown,
  ArrowUp,
  BookOpen,
  CheckCircle2,
  ChevronRight,
  ClipboardList,
  FilePlus2,
  FileText,
  FolderPlus,
  FolderOpen,
  GitBranch,
  History,
  Indent,
  ListTree,
  Loader2,
  Outdent,
  Pencil,
  Plus,
  Save,
  StickyNote,
  Tags,
  Trash2,
  Upload
} from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import type { KeyboardEvent } from "react";
import type { EntityLink, NoteCard, RelationType, SourceKind, SourceNode } from "../types";
import { EntityLinksPanel } from "./EntityLinksPanel";
import { MarkdownPreview } from "./MarkdownPreview";
import { NoteEditForm } from "./NoteEditForm";
import { LifecycleBadge, StatusBadge } from "./StatusBadge";
import { defaultStatusForKind, statusOptionsForKind } from "../lib/lifecycle";
import { useSecondClickConfirm } from "../lib/useSecondClickConfirm";

type Props = {
  sources: SourceNode[];
  notes: NoteCard[];
  selectedSourceId: string;
  onSelectSource: (id: string) => void;
  onCreateSource: (input: { title: string; kind: SourceKind; parentId?: string }) => Promise<string | undefined | void>;
  onUpdateSource: (
    id: string,
    input: { title?: string; kind?: SourceKind; progress?: number; status?: string; parentId?: string | null; properties?: Record<string, unknown> }
  ) => Promise<void>;
  onArchiveSource?: (id: string) => Promise<void>;
  onCapture: (input: {
    kind: NoteCard["kind"];
    content: string;
    sourceId: string;
    status?: string;
    relationType?: string;
    relationTargetId?: string;
    properties?: Record<string, unknown>;
  }) => Promise<{ objectId?: string } | void>;
  onUpdateNote?: (id: string, input: { content: string }) => Promise<void> | void;
  onCreateLink?: (objectId: string, input: { url: string; label?: string }) => Promise<void>;
  onLoadLinks?: (objectId: string) => Promise<void>;
  onDeleteLink?: (objectId: string, linkId: string) => Promise<void>;
  linksByObject?: Record<string, EntityLink[]>;
  onCreateRelation?: (input: {
    sourceObjectId: string;
    relationType: string;
    targetObjectId: string;
    properties?: Record<string, unknown>;
  }) => Promise<void>;
  relationTypes?: RelationType[];
  sourceError?: string;
  captureError?: string;
  linkError?: string;
  relationError?: string;
};

type FlatSource = SourceNode & {
  depth: number;
  childCount: number;
};

type TocLine = {
  title: string;
  depth: number;
};

type PropertyPair = {
  id: string;
  key: string;
  value: string;
};

type LinkDraft = {
  id: string;
  url: string;
  label: string;
};

type RelationDraft = {
  type: string;
  targetId: string;
  properties: PropertyPair[];
};

const sourceKindOptions: SourceKind[] = ["book", "module", "lesson", "topic", "article", "course"];
const noteKindOptions: Array<{ value: NoteCard["kind"]; label: string }> = [
  { value: "note", label: "Note" },
  { value: "task", label: "Task" },
  { value: "quote", label: "Quote" },
  { value: "practice", label: "Practice" },
  { value: "exercise", label: "Exercise" },
  { value: "reflection", label: "Reflection" }
];
const draftStorageKey = "human-note.bookmap.drafts";
const fallbackRelationTypes: RelationType[] = [
  { id: "relation_belongs_to", slug: "belongs_to", name: "Belongs to", description: "", directional: true, system: true },
  { id: "relation_mentions", slug: "mentions", name: "Mentions", description: "", directional: true, system: true },
  { id: "relation_supports", slug: "supports", name: "Supports", description: "", directional: true, system: true },
  { id: "relation_contradicts", slug: "contradicts", name: "Contradicts", description: "", directional: false, system: true },
  { id: "relation_turns_into_task", slug: "turns_into_task", name: "Turns into task", description: "", directional: true, system: true },
  { id: "relation_enriches", slug: "enriches", name: "Enriches", description: "", directional: true, system: true }
];

function buildChildren(sources: SourceNode[]) {
  const children = new Map<string, SourceNode[]>();

  sources.forEach((source) => {
    if (!source.parentId) {
      return;
    }

    children.set(source.parentId, [...(children.get(source.parentId) ?? []), source]);
  });

  return children;
}

function flattenFrom(root: SourceNode, children: Map<string, SourceNode[]>) {
  const output: FlatSource[] = [];

  const visit = (node: SourceNode, depth: number) => {
    const childItems = children.get(node.id) ?? [];
    output.push({ ...node, depth, childCount: childItems.length });
    childItems.forEach((child) => visit(child, depth + 1));
  };

  visit(root, 0);
  return output;
}

function titleFromPath(path: string) {
  const filename = path.split("/").filter(Boolean).at(-1) ?? path;
  return filename.replace(/\.[^.]+$/, "").replace(/[-_]+/g, " ").trim() || "Untitled";
}

function stripTocMarker(value: string) {
  return value
    .replace(/^#{1,6}\s+/, "")
    .replace(/^[-*+]\s+/, "")
    .replace(/^\d+(?:\.\d+)*[.)]?\s+/, "")
    .trim();
}

function parseTocLines(value: string) {
  const lines: TocLine[] = [];
  let previousDepth = 0;

  value.split(/\r?\n/).forEach((line) => {
    if (!line.trim()) {
      return;
    }

    const leading = line.match(/^\s*/)?.[0] ?? "";
    const indentDepth = Math.floor((leading.replace(/\t/g, "  ").length || 0) / 2);
    const trimmed = line.trim();
    const numbering = trimmed.match(/^(\d+(?:\.\d+)*)(?:[.)])?\s+/)?.[1];
    const numberingDepth = numbering ? Math.max(0, numbering.split(".").length - 1) : 0;
    const title = stripTocMarker(trimmed);

    if (!title) {
      return;
    }

    const rawDepth = Math.max(indentDepth, numberingDepth);
    const depth = lines.length === 0 ? 0 : Math.min(rawDepth, previousDepth + 1);
    previousDepth = depth;
    lines.push({ title, depth });
  });

  return lines;
}

function getRelativePath(file: File) {
  return (file as File & { webkitRelativePath?: string }).webkitRelativePath || file.name;
}

function childKindFor(parent?: SourceNode, root?: SourceNode): SourceKind {
  if (parent?.kind === "book") {
    return "book";
  }

  if (root?.kind === "book") {
    return "book";
  }

  if (!parent) {
    return "book";
  }

  if (parent.kind === "course") {
    return "module";
  }

  if (parent.kind === "module") {
    return "lesson";
  }

  return "topic";
}

function tocKindForDepth(depth: number, root?: SourceNode): SourceKind {
  if (root?.kind === "book") {
    return "book";
  }

  if (depth === 0) {
    return "module";
  }

  if (depth === 1) {
    return "lesson";
  }

  return "topic";
}

function loadDrafts() {
  try {
    return JSON.parse(window.localStorage.getItem(draftStorageKey) ?? "{}") as Record<string, string>;
  } catch {
    return {};
  }
}

function makeId() {
  return Math.random().toString(36).slice(2);
}

function objectToPropertyPairs(properties?: Record<string, unknown>) {
  return Object.entries(properties ?? {})
    .filter(([key]) => key !== "progress")
    .map(([key, value]) => ({ id: makeId(), key, value: String(value ?? "") }));
}

function propertyPairsToObject(pairs: PropertyPair[]) {
  return pairs.reduce<Record<string, string>>((properties, pair) => {
    const key = pair.key.trim();
    if (key) {
      properties[key] = pair.value.trim();
    }
    return properties;
  }, {});
}

function defaultPropertiesFor(kind?: SourceKind) {
  if (kind === "book" || kind === "article") {
    return [
      { id: makeId(), key: "author", value: "" },
      { id: makeId(), key: "publisher", value: "" },
      { id: makeId(), key: "year", value: "" }
    ];
  }

  if (kind === "course") {
    return [
      { id: makeId(), key: "instructor", value: "" },
      { id: makeId(), key: "platform", value: "" },
      { id: makeId(), key: "url", value: "" }
    ];
  }

  return [{ id: makeId(), key: "", value: "" }];
}

function updatePair(pairs: PropertyPair[], id: string, field: "key" | "value", value: string) {
  return pairs.map((pair) => (pair.id === id ? { ...pair, [field]: value } : pair));
}

export function BookMapPanel({
  sources,
  notes,
  selectedSourceId,
  onSelectSource,
  onCreateSource,
  onUpdateSource,
  onArchiveSource,
  onCapture,
  onUpdateNote,
  onCreateLink,
  onLoadLinks,
  onDeleteLink,
  linksByObject = {},
  onCreateRelation,
  relationTypes = fallbackRelationTypes,
  sourceError,
  captureError,
  linkError,
  relationError
}: Props) {
  const folderInputRef = useRef<HTMLInputElement>(null);
  const titleInputRefs = useRef(new Map<string, HTMLInputElement>());
  const [rootId, setRootId] = useState("");
  const [titles, setTitles] = useState<Record<string, string>>({});
  const [drafts, setDrafts] = useState<Record<string, string>>(() => loadDrafts());
  const [pendingTitleFocusId, setPendingTitleFocusId] = useState("");
  const [savingTitleId, setSavingTitleId] = useState("");
  const [savingStructureId, setSavingStructureId] = useState("");
  const [savingNote, setSavingNote] = useState(false);
  const [editingNoteId, setEditingNoteId] = useState("");
  const [importing, setImporting] = useState(false);
  const [importStatus, setImportStatus] = useState("");
  const [tocOpen, setTocOpen] = useState(false);
  const [tocText, setTocText] = useState("");
  const [savingToc, setSavingToc] = useState(false);
  const [sourceProperties, setSourceProperties] = useState<PropertyPair[]>([]);
  const [savingSourceProperties, setSavingSourceProperties] = useState(false);
  const [noteProperties, setNoteProperties] = useState<PropertyPair[]>([{ id: makeId(), key: "", value: "" }]);
  const [noteLinks, setNoteLinks] = useState<LinkDraft[]>([{ id: makeId(), url: "", label: "" }]);
  const [noteRelation, setNoteRelation] = useState<RelationDraft>({
    type: "mentions",
    targetId: "",
    properties: [{ id: makeId(), key: "", value: "" }]
  });
  const [sourceRelation, setSourceRelation] = useState<RelationDraft>({
    type: "mentions",
    targetId: "",
    properties: [{ id: makeId(), key: "", value: "" }]
  });
  const deleteSourceConfirm = useSecondClickConfirm();
  const [savingSourceRelation, setSavingSourceRelation] = useState(false);
  const [kindOverrides, setKindOverrides] = useState<Record<string, SourceKind>>({});
  const [rightTab, setRightTab] = useState<"note" | "manage" | "history">("note");
  const [noteKind, setNoteKind] = useState<NoteCard["kind"]>("note");

  const children = useMemo(() => buildChildren(sources), [sources]);
  const roots = useMemo(
    () => {
      const bookRoots = sources.filter((source) => source.kind === "book" || source.kind === "course");
      return bookRoots.length > 0 ? bookRoots : sources.filter((source) => !source.parentId);
    },
    [sources]
  );

  const root = useMemo(
    () => sources.find((source) => source.id === rootId) ?? roots[0] ?? sources[0],
    [rootId, roots, sources]
  );

  const flatSources = useMemo(() => {
    if (!root) {
      return [];
    }

    return flattenFrom(root, children);
  }, [children, root]);

  const selectedSource = useMemo(
    () => flatSources.find((source) => source.id === selectedSourceId) ?? flatSources[0],
    [flatSources, selectedSourceId]
  );

  const selectedIndex = selectedSource ? flatSources.findIndex((source) => source.id === selectedSource.id) : -1;
  const sourceById = useMemo(() => new Map(flatSources.map((source) => [source.id, source])), [flatSources]);
  const bookNotes = useMemo(() => {
    const sourceIds = new Set(flatSources.map((source) => source.id));
    return notes.filter((note) => sourceIds.has(note.sourceId));
  }, [flatSources, notes]);
  const relationTargets = useMemo(
    () => [
      ...flatSources.map((source) => ({ id: source.id, label: `${source.kind} - ${source.title}` })),
      ...bookNotes.map((note) => ({ id: note.id, label: `note - ${note.title}` }))
    ],
    [bookNotes, flatSources]
  );
  const relationTypeOptions = relationTypes.length ? relationTypes : fallbackRelationTypes;
  const noteDraft = selectedSource ? drafts[selectedSource.id] ?? "" : "";

  const effectiveSource = (source?: SourceNode) =>
    source ? { ...source, kind: kindOverrides[source.id] ?? source.kind } : undefined;

  useEffect(() => {
    folderInputRef.current?.setAttribute("webkitdirectory", "");
    folderInputRef.current?.setAttribute("directory", "");
  }, [sources.length]);

  useEffect(() => {
    if (rootId || !selectedSourceId) {
      return;
    }

    const selected = sources.find((source) => source.id === selectedSourceId);
    if (!selected) {
      return;
    }

    let current = selected;
    while (current.parentId) {
      const parent = sources.find((source) => source.id === current.parentId);
      if (!parent) {
        break;
      }
      current = parent;
    }
    setRootId(current.id);
  }, [rootId, selectedSourceId, sources]);

  useEffect(() => {
    if (!selectedSourceId || flatSources.some((source) => source.id === selectedSourceId)) {
      return;
    }

    const selected = sources.find((source) => source.id === selectedSourceId);
    if (!selected) {
      return;
    }

    let current = selected;
    while (current.parentId) {
      const parent = sources.find((source) => source.id === current.parentId);
      if (!parent) {
        break;
      }
      current = parent;
    }
    setRootId(current.id);
  }, [flatSources, selectedSourceId, sources]);

  useEffect(() => {
    if (root && !selectedSource) {
      onSelectSource(root.id);
    }
  }, [onSelectSource, root, selectedSource]);

  useEffect(() => {
    window.localStorage.setItem(draftStorageKey, JSON.stringify(drafts));
  }, [drafts]);

  useEffect(() => {
    if (!pendingTitleFocusId) {
      return;
    }

    const frame = window.requestAnimationFrame(() => {
      const input = titleInputRefs.current.get(pendingTitleFocusId);
      input?.focus();
      input?.select();
      setPendingTitleFocusId("");
    });

    return () => window.cancelAnimationFrame(frame);
  }, [flatSources, pendingTitleFocusId]);

  useEffect(() => {
    setKindOverrides((current) => {
      const next = { ...current };
      sources.forEach((source) => {
        if (next[source.id] === source.kind) {
          delete next[source.id];
        }
      });
      return next;
    });
  }, [sources]);

  useEffect(() => {
    setSourceProperties(
      selectedSource?.properties && Object.keys(selectedSource.properties).some((key) => key !== "progress")
        ? objectToPropertyPairs(selectedSource.properties)
        : defaultPropertiesFor(selectedSource?.kind)
    );
    setSourceRelation({
      type: "mentions",
      targetId: "",
      properties: [{ id: makeId(), key: "", value: "" }]
    });
  }, [selectedSource?.id, selectedSource?.kind, selectedSource?.properties]);

  const commitTitle = async (source: SourceNode) => {
    const nextTitle = (titles[source.id] ?? source.title).trim();

    if (!nextTitle) {
      setTitles((current) => ({ ...current, [source.id]: "" }));
      return;
    }

    if (nextTitle === source.title) {
      setTitles((current) => ({ ...current, [source.id]: source.title }));
      return;
    }

    setSavingTitleId(source.id);

    try {
      await onUpdateSource(source.id, { title: nextTitle });
    } finally {
      setSavingTitleId("");
    }
  };

  const handleCreateNode = async (parent?: SourceNode, title = " ") => {
    const createdId = await onCreateSource({
      title,
      kind: childKindFor(effectiveSource(parent), effectiveSource(root)),
      parentId: parent?.id
    });

    if (createdId) {
      onSelectSource(createdId);
      setPendingTitleFocusId(createdId);
      setTitles((current) => ({ ...current, [createdId]: title.trim() ? title : "" }));
    }
  };

  const handleCreateSibling = async (source: SourceNode) => {
    await handleCreateNode(
      source.parentId ? sources.find((item) => item.id === source.parentId) : undefined,
      " "
    );
  };

  const handleSaveSourceProperties = async () => {
    if (!selectedSource) {
      return;
    }

    setSavingSourceProperties(true);

    try {
      await onUpdateSource(selectedSource.id, { properties: propertyPairsToObject(sourceProperties) });
    } finally {
      setSavingSourceProperties(false);
    }
  };

  const handleSaveSourceRelation = async () => {
    if (!selectedSource || !sourceRelation.targetId || !onCreateRelation) {
      return;
    }

    setSavingSourceRelation(true);

    try {
      await onCreateRelation({
        sourceObjectId: selectedSource.id,
        relationType: sourceRelation.type,
        targetObjectId: sourceRelation.targetId,
        properties: propertyPairsToObject(sourceRelation.properties)
      });
      setSourceRelation({
        type: "mentions",
        targetId: "",
        properties: [{ id: makeId(), key: "", value: "" }]
      });
    } finally {
      setSavingSourceRelation(false);
    }
  };

  const canDeleteSource = (source?: FlatSource) => {
    if (!source || !onArchiveSource) {
      return false;
    }

    const hasLoadedLinks = (linksByObject[source.id] ?? []).length > 0;
    return source.childCount === 0 && source.noteCount === 0 && !hasLoadedLinks;
  };

  const handleDeleteSource = async (source = selectedSource) => {
    if (!source || !canDeleteSource(source)) {
      return;
    }

    await onArchiveSource?.(source.id);
  };

  const handleSaveToc = async () => {
    if (!root || savingToc) {
      return;
    }

    const lines = parseTocLines(tocText);
    if (lines.length === 0) {
      return;
    }

    setSavingToc(true);
    setImportStatus("Đang lưu mục lục");

    try {
      const parents = new Map<number, string>([[-1, root.id]]);

      for (let index = 0; index < lines.length; index += 1) {
        const line = lines[index];
        const parentId = parents.get(line.depth - 1) ?? root.id;
        setImportStatus(`Đang tạo ${index + 1}/${lines.length}: ${line.title}`);
        const createdId = await onCreateSource({
          title: line.title,
          kind: tocKindForDepth(line.depth, effectiveSource(root)),
          parentId
        });

        if (createdId) {
          parents.set(line.depth, createdId);
          [...parents.keys()].forEach((depth) => {
            if (depth > line.depth) {
              parents.delete(depth);
            }
          });
        }
      }

      setImportStatus(`Đã lưu ${lines.length} mục trong mục lục`);
      setTocText("");
      setTocOpen(false);
    } finally {
      setSavingToc(false);
    }
  };

  const handleIndent = async (source: SourceNode) => {
    const index = flatSources.findIndex((item) => item.id === source.id);
    const previous = flatSources[index - 1];

    if (!previous || previous.id === source.parentId) {
      return;
    }

    setSavingStructureId(source.id);

    try {
      await onUpdateSource(source.id, { parentId: previous.id });
    } finally {
      setSavingStructureId("");
    }
  };

  const handleOutdent = async (source: SourceNode) => {
    if (!source.parentId) {
      return;
    }

    const parent = sources.find((item) => item.id === source.parentId);
    const nextParentId = parent?.parentId ?? null;

    setSavingStructureId(source.id);

    try {
      await onUpdateSource(source.id, { parentId: nextParentId });
      if (nextParentId === null) {
        setRootId(source.id);
      }
    } finally {
      setSavingStructureId("");
    }
  };

  const handleMoveSelection = (direction: -1 | 1) => {
    const next = flatSources[selectedIndex + direction];
    if (next) {
      onSelectSource(next.id);
    }
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>, source: SourceNode) => {
    if (event.key === "Enter") {
      event.preventDefault();
      void commitTitle(source).then(() => handleCreateSibling(source));
      return;
    }

    if (event.key === "Tab") {
      event.preventDefault();
      void (event.shiftKey ? handleOutdent(source) : handleIndent(source));
      return;
    }

    if (event.key === "ArrowUp") {
      event.preventDefault();
      handleMoveSelection(-1);
      return;
    }

    if (event.key === "ArrowDown") {
      event.preventDefault();
      handleMoveSelection(1);
    }
  };

  const handleSaveNote = async () => {
    if (!selectedSource || !noteDraft.trim()) {
      return;
    }

    const content = noteDraft.trim();
    setSavingNote(true);

    try {
      const result = await onCapture({
        kind: noteKind,
        content,
        sourceId: selectedSource.id,
        relationType: "belongs_to",
        relationTargetId: selectedSource.id,
        properties: propertyPairsToObject(noteProperties)
      });
      const noteId = result?.objectId;

      if (noteId && onCreateLink) {
        for (const link of noteLinks) {
          if (link.url.trim()) {
            await onCreateLink(noteId, { url: link.url.trim(), label: link.label.trim() || undefined });
          }
        }
      }

      if (noteId && noteRelation.targetId && onCreateRelation) {
        await onCreateRelation({
          sourceObjectId: noteId,
          relationType: noteRelation.type,
          targetObjectId: noteRelation.targetId,
          properties: propertyPairsToObject(noteRelation.properties)
        });
      }

      setDrafts((current) => ({ ...current, [selectedSource.id]: "" }));
      setNoteProperties([{ id: makeId(), key: "", value: "" }]);
      setNoteLinks([{ id: makeId(), url: "", label: "" }]);
      setNoteRelation({ type: "mentions", targetId: "", properties: [{ id: makeId(), key: "", value: "" }] });
    } finally {
      setSavingNote(false);
    }
  };

  const handleFolderImport = async (files: FileList | null) => {
    const fileItems = Array.from(files ?? []);
    if (fileItems.length === 0) {
      return;
    }

    const firstPath = getRelativePath(fileItems[0]);
    const bookTitle = firstPath.split("/").filter(Boolean)[0] || "Imported book";
    setImporting(true);
    setImportStatus("Dang tao book");

    try {
      const bookId = await onCreateSource({ title: bookTitle, kind: "book" });
      if (!bookId) {
        return;
      }

      setRootId(bookId);
      onSelectSource(bookId);

      const folderIds = new Map<string, string>();
      const sortedFiles = fileItems
        .map((file) => getRelativePath(file))
        .filter(Boolean)
        .sort((a, b) => a.localeCompare(b));

      for (const path of sortedFiles) {
        const parts = path.split("/").filter(Boolean).slice(1);
        if (parts.length === 0) {
          continue;
        }

        let parentId = bookId;
        let folderKey = "";
        const folderParts = parts.slice(0, -1);

        for (const folder of folderParts) {
          folderKey = folderKey ? `${folderKey}/${folder}` : folder;
          const existingId = folderIds.get(folderKey);
          if (existingId) {
            parentId = existingId;
            continue;
          }

          setImportStatus(`Dang tao muc ${folder}`);
          const createdFolderId = await onCreateSource({ title: titleFromPath(folder), kind: "book", parentId });
          if (createdFolderId) {
            folderIds.set(folderKey, createdFolderId);
            parentId = createdFolderId;
          }
        }

        const fileTitle = titleFromPath(parts.at(-1) ?? path);
        setImportStatus(`Dang tao bai ${fileTitle}`);
        await onCreateSource({ title: fileTitle, kind: "book", parentId });
      }

      setImportStatus(`Da nap ${sortedFiles.length} file`);
    } finally {
      setImporting(false);
      if (folderInputRef.current) {
        folderInputRef.current.value = "";
      }
    }
  };

  if (sources.length === 0) {
    return (
      <section className="rounded-lg border border-line bg-white p-5 shadow-panel">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg border border-blue-100 bg-blue-50 text-brand">
              <BookOpen size={20} aria-hidden="true" />
            </div>
            <div>
              <h1 className="text-lg font-normal">Book Map</h1>
              <p className="text-sm font-medium text-muted">Tạo book đầu tiên để nạp cây thư mục sách.</p>
            </div>
          </div>
          <input
            ref={folderInputRef}
            className="hidden"
            type="file"
            multiple
            onChange={(event) => void handleFolderImport(event.target.files)}
          />
          <div className="flex flex-wrap items-center gap-2">
            <button
              className="inline-flex h-10 items-center gap-2 rounded-lg border border-line bg-white px-4 text-sm font-normal text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:text-slate-300"
              type="button"
              disabled={importing}
              onClick={() => folderInputRef.current?.click()}
            >
              {importing ? <Loader2 className="animate-spin" size={16} aria-hidden="true" /> : <Upload size={16} aria-hidden="true" />}
              Nạp thư mục
            </button>
            <button
              className="inline-flex h-10 items-center gap-2 rounded-lg bg-brand px-4 text-sm font-normal text-white hover:bg-blue-700"
              type="button"
              onClick={() => void handleCreateNode(undefined)}
            >
              <Plus size={17} aria-hidden="true" />
              Tạo book
            </button>
          </div>
        </div>
        {importStatus ? (
          <p className="mt-4 inline-flex items-center gap-2 rounded-md border border-blue-100 bg-blue-50 px-3 py-1.5 text-xs font-normal text-brand">
            {importing ? <Loader2 className="animate-spin" size={14} aria-hidden="true" /> : <CheckCircle2 size={14} aria-hidden="true" />}
            {importStatus}
          </p>
        ) : null}
      </section>
    );
  }

  return (
    <div className="grid min-h-[calc(100vh-6rem)] gap-4 xl:grid-cols-2">
      <section className="min-w-0 overflow-hidden rounded-lg border border-line bg-white shadow-panel">
        <div className="border-b border-line px-4 py-3">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex min-w-0 items-center gap-3">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border border-blue-100 bg-blue-50 text-brand">
                <ListTree size={20} aria-hidden="true" />
              </div>
              <div className="min-w-0">
                <h1 className="truncate text-lg font-normal">Book Map</h1>
                <p className="truncate text-sm font-medium text-muted">{root?.title ?? "Chưa có book"}</p>
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <select
                className="h-10 w-full min-w-0 rounded-lg border border-line bg-white px-3 text-sm font-normal text-slate-700 sm:w-auto sm:min-w-[180px]"
                value={root?.id ?? ""}
                onChange={(event) => {
                  setRootId(event.target.value);
                  onSelectSource(event.target.value);
                }}
                aria-label="Book root"
              >
                {roots.map((item) => (
                  <option key={item.id} value={item.id}>
                    {item.title}
                  </option>
                ))}
              </select>
              <input
                ref={folderInputRef}
                className="hidden"
                type="file"
                multiple
                onChange={(event) => void handleFolderImport(event.target.files)}
              />
              <button
                className="inline-flex h-10 items-center gap-2 rounded-lg border border-line bg-white px-3 text-sm font-normal text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:text-slate-300"
                type="button"
                disabled={importing}
                onClick={() => folderInputRef.current?.click()}
              >
                {importing ? <Loader2 className="animate-spin" size={16} aria-hidden="true" /> : <Upload size={16} aria-hidden="true" />}
                Nạp thư mục
              </button>
              <button
                className={`inline-flex h-10 items-center gap-2 rounded-lg border px-3 text-sm font-normal ${
                  tocOpen
                    ? "border-blue-200 bg-blue-50 text-brand"
                    : "border-line bg-white text-slate-700 hover:bg-slate-50"
                }`}
                type="button"
                onClick={() => setTocOpen((value) => !value)}
              >
                <ClipboardList size={16} aria-hidden="true" />
                Mục lục
              </button>
              <button
                className="inline-flex h-10 items-center gap-2 rounded-lg bg-brand px-3 text-sm font-normal text-white hover:bg-blue-700"
                type="button"
                onClick={() => void handleCreateNode(root)}
              >
                <FilePlus2 size={16} aria-hidden="true" />
                Thêm mục
              </button>
            </div>
          </div>

          {importStatus ? (
            <p className="mt-3 inline-flex items-center gap-2 rounded-md border border-blue-100 bg-blue-50 px-3 py-1.5 text-xs font-normal text-brand">
              {importing ? <Loader2 className="animate-spin" size={14} aria-hidden="true" /> : <CheckCircle2 size={14} aria-hidden="true" />}
              {importStatus}
            </p>
          ) : null}
          {sourceError ? (
            <p className="mt-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm font-normal text-red-700">{sourceError}</p>
          ) : null}
          {tocOpen ? (
            <div className="mt-3 rounded-lg border border-line bg-slate-50 p-3">
              <label className="text-xs font-normal uppercase tracking-wide text-muted" htmlFor="book-toc">
                Lưu mục lục đầy đủ vào {root?.title}
              </label>
              <textarea
                id="book-toc"
                className="mt-2 min-h-36 w-full resize-y rounded-lg border border-line bg-white p-3 text-sm leading-6 text-ink placeholder:text-slate-400"
                value={tocText}
                onChange={(event) => setTocText(event.target.value)}
                placeholder={"Phần 1\n  Chương 1\n    Bài 1.1\n1. Tổng quan\n1.1 Khái niệm"}
              />
              <div className="mt-2 flex flex-wrap items-center justify-between gap-2">
                <span className="text-xs font-normal text-muted">{parseTocLines(tocText).length} mục sẽ được tạo</span>
                <button
                  className="inline-flex h-9 items-center gap-2 rounded-lg bg-brand px-3 text-sm font-normal text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:bg-slate-300"
                  type="button"
                  disabled={!root || parseTocLines(tocText).length === 0 || savingToc}
                  onClick={() => void handleSaveToc()}
                >
                  {savingToc ? <Loader2 className="animate-spin" size={16} aria-hidden="true" /> : <Save size={16} aria-hidden="true" />}
                  Lưu mục lục
                </button>
              </div>
            </div>
          ) : null}
        </div>

        <div className="overflow-x-auto">
          <div className="min-w-[600px] border-b border-line bg-slate-50 px-4 py-2">
            <div className="grid grid-cols-[minmax(180px,1fr)_104px_184px] gap-3 text-xs font-normal uppercase tracking-wide text-muted">
              <span>Xuong song</span>
              <span>Loai</span>
              <span className="text-right">Nut</span>
            </div>
          </div>

          <div className="scrollbar-thin max-h-[calc(100vh-15rem)] min-w-[600px] overflow-y-auto">
            {flatSources.map((source) => {
              const isSelected = selectedSource?.id === source.id;
              const isBusy = savingTitleId === source.id || savingStructureId === source.id;
              const Icon = source.kind === "book" ? BookOpen : source.kind === "module" ? FolderOpen : FileText;

              return (
                <div
                  key={source.id}
                  className={`grid grid-cols-[minmax(180px,1fr)_104px_184px] items-center gap-3 border-b border-line px-4 py-2 transition ${
                    isSelected ? "bg-blue-50" : "bg-white hover:bg-slate-50"
                  }`}
                  onClick={() => onSelectSource(source.id)}
                >
                  <div className="min-w-0" style={{ paddingLeft: `${Math.min(source.depth, 6) * 22}px` }}>
                    <div className="flex min-w-0 items-center gap-2">
                      {source.depth > 0 ? <ChevronRight className="shrink-0 text-slate-300" size={15} aria-hidden="true" /> : null}
                      <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md border border-line bg-white text-slate-600">
                        <Icon size={15} aria-hidden="true" />
                      </div>
                      <input
                        ref={(element) => {
                          if (element) {
                            titleInputRefs.current.set(source.id, element);
                          } else {
                            titleInputRefs.current.delete(source.id);
                          }
                        }}
                        className={`h-10 min-w-0 flex-1 rounded-md border bg-white px-2 text-sm font-normal text-ink outline-none ${
                          isSelected ? "border-blue-200" : "border-transparent"
                        }`}
                        value={titles[source.id] ?? source.title}
                        onFocus={() => onSelectSource(source.id)}
                        onChange={(event) => setTitles((current) => ({ ...current, [source.id]: event.target.value }))}
                        onBlur={() => void commitTitle(source)}
                        onKeyDown={(event) => handleKeyDown(event, source)}
                        aria-label={`Title ${source.title}`}
                      />
                    </div>
                    <p className="mt-1 truncate pl-11 text-[11px] leading-4 text-muted">Tạo lúc {source.createdAt}</p>
                  </div>

                  <select
                    className="h-10 min-w-0 rounded-md border border-line bg-white px-2 text-xs font-normal text-slate-700"
                    value={kindOverrides[source.id] ?? source.kind}
                    onClick={(event) => event.stopPropagation()}
                    onChange={(event) => {
                      const kind = event.target.value as SourceKind;
                      setKindOverrides((current) => ({ ...current, [source.id]: kind }));
                      void onUpdateSource(source.id, { kind });
                    }}
                    aria-label={`Kind ${source.title}`}
                  >
                    {sourceKindOptions.map((kind) => (
                      <option key={kind} value={kind}>
                        {kind}
                      </option>
                    ))}
                  </select>

                  <div className="flex items-center justify-end gap-1">
                    <button
                      className="inline-flex h-9 w-9 items-center justify-center rounded-md border border-line bg-white text-slate-600 hover:bg-slate-50 disabled:text-slate-300"
                      type="button"
                      title="Add child"
                      aria-label={`Add child to ${source.title}`}
                      disabled={isBusy}
                      onClick={(event) => {
                        event.stopPropagation();
                        void handleCreateNode(source);
                      }}
                    >
                      <FolderPlus size={15} aria-hidden="true" />
                    </button>
                    <button
                      className="inline-flex h-9 w-9 items-center justify-center rounded-md border border-line bg-white text-slate-600 hover:bg-slate-50 disabled:text-slate-300"
                      type="button"
                      disabled={source.depth === 0}
                      title="Outdent"
                      aria-label={`Outdent ${source.title}`}
                      onClick={(event) => {
                        event.stopPropagation();
                        void handleOutdent(source);
                      }}
                    >
                      <Outdent size={15} aria-hidden="true" />
                    </button>
                    <button
                      className="inline-flex h-9 w-9 items-center justify-center rounded-md border border-line bg-white text-slate-600 hover:bg-slate-50 disabled:text-slate-300"
                      type="button"
                      disabled={flatSources.findIndex((item) => item.id === source.id) === 0}
                      title="Indent"
                      aria-label={`Indent ${source.title}`}
                      onClick={(event) => {
                        event.stopPropagation();
                        void handleIndent(source);
                      }}
                    >
                      <Indent size={15} aria-hidden="true" />
                    </button>
                    <button
                      className="inline-flex h-9 w-9 items-center justify-center rounded-md border border-red-200 bg-white text-red-600 hover:bg-red-50 disabled:cursor-not-allowed disabled:border-line disabled:text-slate-300"
                      type="button"
                      disabled={!canDeleteSource(source)}
                      title={canDeleteSource(source) ? "Delete empty item" : "Only empty items can be deleted"}
                      aria-label={`Delete ${source.title}`}
                      onClick={(event) => {
                        event.stopPropagation();
                        deleteSourceConfirm.request(source.id, () => void handleDeleteSource(source));
                      }}
                    >
                      {deleteSourceConfirm.pendingId === source.id ? <CheckCircle2 size={15} aria-hidden="true" /> : <Trash2 size={15} aria-hidden="true" />}
                    </button>
                    {isBusy ? <Loader2 className="animate-spin text-brand" size={16} aria-hidden="true" /> : <span className="w-5 text-right text-xs font-normal text-muted">{source.childCount}</span>}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <aside className="min-w-0 overflow-hidden rounded-lg border border-line bg-white shadow-panel xl:sticky xl:top-20 xl:h-[calc(100vh-6rem)]">
        <div className="border-b border-line px-4 py-3">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <h2 className="truncate text-sm font-normal">{selectedSource?.title ?? "Chưa chọn mục"}</h2>
              <p className="mt-1 text-xs font-medium text-muted">
                {selectedSource ? `Tạo lúc ${selectedSource.createdAt}` : "Tab note của mục đang chọn"}
              </p>
            </div>
            {selectedSource ? (
              <div className="flex shrink-0 flex-col items-end gap-1.5">
                <StatusBadge kind={selectedSource.kind} />
                <LifecycleBadge kind={selectedSource.kind} status={selectedSource.status} />
              </div>
            ) : null}
          </div>
          {selectedSource ? (
            <select
              className="mt-2 w-full rounded-md border border-line bg-slate-50 px-2 py-1 text-[11px] font-semibold text-slate-600"
              value={selectedSource.status ?? defaultStatusForKind(selectedSource.kind)}
              onChange={(event) => onUpdateSource(selectedSource.id, { status: event.target.value })}
            >
              {statusOptionsForKind(selectedSource.kind).map((option) => (
                <option key={option.slug} value={option.slug}>
                  {option.label}
                </option>
              ))}
            </select>
          ) : null}
        </div>

        <div className="scrollbar-thin flex h-full flex-col overflow-y-auto p-4">
          <div className="mb-4 grid grid-cols-3 rounded-lg border border-line bg-slate-50 p-1">
            {(["note", "manage", "history"] as const).map((tab) => (
              <button
                key={tab}
                className={`h-9 rounded-md text-sm font-normal transition ${
                  rightTab === tab ? "bg-white text-ink shadow-sm" : "text-muted hover:text-ink"
                }`}
                type="button"
                onClick={() => setRightTab(tab)}
              >
                {tab === "note" ? "Note" : tab === "manage" ? "Manage" : "History"}
              </button>
            ))}
          </div>
          {rightTab === "manage" ? (
            <>
          <div className="grid grid-cols-2 gap-2">
            <button
              className="inline-flex h-10 items-center justify-center gap-2 rounded-lg border border-line bg-white px-3 text-sm font-normal text-slate-600 hover:bg-slate-50 disabled:text-slate-300"
              type="button"
              title="Previous"
              aria-label="Previous item"
              disabled={selectedIndex <= 0}
              onClick={() => handleMoveSelection(-1)}
            >
              <ArrowUp size={16} aria-hidden="true" />
              Truoc
            </button>
            <button
              className="inline-flex h-10 items-center justify-center gap-2 rounded-lg border border-line bg-white px-3 text-sm font-normal text-slate-600 hover:bg-slate-50 disabled:text-slate-300"
              type="button"
              title="Next"
              aria-label="Next item"
              disabled={selectedIndex < 0 || selectedIndex >= flatSources.length - 1}
              onClick={() => handleMoveSelection(1)}
            >
              <ArrowDown size={16} aria-hidden="true" />
              Sau
            </button>
            <button
              className="inline-flex h-10 items-center justify-center gap-2 rounded-lg border border-line bg-white px-3 text-sm font-normal text-slate-600 hover:bg-slate-50 disabled:text-slate-300"
              type="button"
              title="Add child"
              aria-label="Add child item"
              disabled={!selectedSource}
              onClick={() => void handleCreateNode(selectedSource)}
            >
              <FolderPlus size={16} aria-hidden="true" />
              Muc con
            </button>
            <button
              className="inline-flex h-10 items-center justify-center gap-2 rounded-lg border border-line bg-white px-3 text-sm font-normal text-slate-600 hover:bg-slate-50 disabled:text-slate-300"
              type="button"
              title="Branch"
              aria-label="Add sibling item"
              disabled={!selectedSource}
              onClick={() => void handleCreateSibling(selectedSource)}
            >
              <GitBranch size={16} aria-hidden="true" />
              Muc ngang
            </button>
            <button
              className="col-span-2 inline-flex h-10 items-center justify-center gap-2 rounded-lg border border-red-200 bg-white px-3 text-sm font-normal text-red-700 hover:bg-red-50 disabled:cursor-not-allowed disabled:border-line disabled:text-slate-300"
              type="button"
              title="Delete empty item"
              aria-label="Delete empty item"
              disabled={!canDeleteSource(selectedSource)}
              onClick={() => selectedSource && deleteSourceConfirm.request(selectedSource.id, () => void handleDeleteSource())}
            >
              <Trash2 size={16} aria-hidden="true" />
              {selectedSource && deleteSourceConfirm.pendingId === selectedSource.id ? "Bấm lần nữa để xóa" : "Xóa mục trống"}
            </button>
          </div>

          <div className="mt-4 rounded-lg border border-line bg-slate-50 p-3">
            <div className="flex items-center justify-between gap-3">
              <div className="flex min-w-0 items-center gap-2">
                <Tags size={15} className="shrink-0 text-muted" aria-hidden="true" />
                <h3 className="truncate text-sm font-normal">Thông tin / thuộc tính</h3>
              </div>
              <button
                className="inline-flex h-8 items-center rounded-lg border border-line bg-white px-3 text-xs font-normal text-slate-700 hover:bg-slate-50"
                type="button"
                disabled={!selectedSource}
                onClick={() => setSourceProperties((current) => [...current, { id: makeId(), key: "", value: "" }])}
              >
                Thêm
              </button>
            </div>
            <div className="mt-3 space-y-2">
              {sourceProperties.map((property) => (
                <div key={property.id} className="grid grid-cols-[minmax(0,0.45fr)_minmax(0,0.55fr)_32px] gap-2">
                  <input
                    className="h-9 min-w-0 rounded-lg border border-line bg-white px-2 text-xs font-normal text-ink"
                    value={property.key}
                    onChange={(event) => setSourceProperties((current) => updatePair(current, property.id, "key", event.target.value))}
                    placeholder="author"
                    aria-label="Tên thuộc tính"
                  />
                  <input
                    className="h-9 min-w-0 rounded-lg border border-line bg-white px-2 text-xs font-normal text-ink"
                    value={property.value}
                    onChange={(event) => setSourceProperties((current) => updatePair(current, property.id, "value", event.target.value))}
                    placeholder="Giá trị"
                    aria-label="Giá trị thuộc tính"
                  />
                  <button
                    className="inline-flex h-9 w-8 items-center justify-center rounded-lg border border-line bg-white text-xs font-normal text-red-600 hover:bg-red-50"
                    type="button"
                    aria-label="Xóa thuộc tính"
                    onClick={() => setSourceProperties((current) => current.filter((item) => item.id !== property.id))}
                  >
                    X
                  </button>
                </div>
              ))}
            </div>
            <button
              className="mt-3 inline-flex h-9 w-full items-center justify-center gap-2 rounded-lg bg-brand px-3 text-xs font-normal text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:bg-slate-300"
              type="button"
              disabled={!selectedSource || savingSourceProperties}
              onClick={() => void handleSaveSourceProperties()}
            >
              {savingSourceProperties ? <Loader2 className="animate-spin" size={14} aria-hidden="true" /> : <Save size={14} aria-hidden="true" />}
              Lưu thuộc tính
            </button>
          </div>

          {selectedSource && onLoadLinks && onCreateLink && onDeleteLink ? (
            <EntityLinksPanel
              objectId={selectedSource.id}
              links={linksByObject[selectedSource.id] ?? []}
              error={linkError}
              onLoad={onLoadLinks}
              onCreate={onCreateLink}
              onDelete={onDeleteLink}
            />
          ) : null}

          <div className="mt-4 rounded-lg border border-line bg-slate-50 p-3">
            <div className="flex items-center gap-2">
              <GitBranch size={15} className="text-muted" aria-hidden="true" />
              <h3 className="text-sm font-normal">Mối quan hệ của mục</h3>
            </div>
            <div className="mt-3 grid gap-2">
              <select
                className="h-9 rounded-lg border border-line bg-white px-2 text-xs font-normal text-slate-700"
                value={sourceRelation.type}
                onChange={(event) => setSourceRelation((current) => ({ ...current, type: event.target.value }))}
                aria-label="Loại quan hệ"
              >
                {relationTypeOptions.map((type) => (
                  <option key={type.id} value={type.slug}>
                    {type.slug}
                  </option>
                ))}
              </select>
              <select
                className="h-9 min-w-0 rounded-lg border border-line bg-white px-2 text-xs font-normal text-slate-700"
                value={sourceRelation.targetId}
                onChange={(event) => setSourceRelation((current) => ({ ...current, targetId: event.target.value }))}
                aria-label="Đối tượng liên quan"
              >
                <option value="">Chọn mục/note liên quan</option>
                {relationTargets
                  .filter((target) => target.id !== selectedSource?.id)
                  .map((target) => (
                    <option key={target.id} value={target.id}>
                      {target.label}
                    </option>
                  ))}
              </select>
            </div>
            <div className="mt-3 space-y-2">
              <div className="flex items-center justify-between gap-2">
                <p className="text-xs font-normal uppercase tracking-wide text-muted">Thuộc tính quan hệ</p>
                <button
                  className="inline-flex h-8 items-center rounded-lg border border-line bg-white px-3 text-xs font-normal text-slate-700 hover:bg-slate-50"
                  type="button"
                  onClick={() =>
                    setSourceRelation((current) => ({
                      ...current,
                      properties: [...current.properties, { id: makeId(), key: "", value: "" }]
                    }))
                  }
                >
                  Thêm
                </button>
              </div>
              {sourceRelation.properties.map((property) => (
                <div key={property.id} className="grid grid-cols-[minmax(0,0.45fr)_minmax(0,0.55fr)_32px] gap-2">
                  <input
                    className="h-9 min-w-0 rounded-lg border border-line bg-white px-2 text-xs font-normal text-ink"
                    value={property.key}
                    onChange={(event) =>
                      setSourceRelation((current) => ({
                        ...current,
                        properties: updatePair(current.properties, property.id, "key", event.target.value)
                      }))
                    }
                    placeholder="role"
                    aria-label="Tên thuộc tính quan hệ"
                  />
                  <input
                    className="h-9 min-w-0 rounded-lg border border-line bg-white px-2 text-xs font-normal text-ink"
                    value={property.value}
                    onChange={(event) =>
                      setSourceRelation((current) => ({
                        ...current,
                        properties: updatePair(current.properties, property.id, "value", event.target.value)
                      }))
                    }
                    placeholder="Giá trị"
                    aria-label="Giá trị thuộc tính quan hệ"
                  />
                  <button
                    className="inline-flex h-9 w-8 items-center justify-center rounded-lg border border-line bg-white text-xs font-normal text-red-600 hover:bg-red-50"
                    type="button"
                    aria-label="Xóa thuộc tính quan hệ"
                    onClick={() =>
                      setSourceRelation((current) => ({
                        ...current,
                        properties: current.properties.filter((item) => item.id !== property.id)
                      }))
                    }
                  >
                    X
                  </button>
                </div>
              ))}
            </div>
            <button
              className="mt-3 inline-flex h-9 w-full items-center justify-center gap-2 rounded-lg border border-line bg-white px-3 text-xs font-normal text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:text-slate-300"
              type="button"
              disabled={!selectedSource || !sourceRelation.targetId || !onCreateRelation || savingSourceRelation}
              onClick={() => void handleSaveSourceRelation()}
            >
              {savingSourceRelation ? <Loader2 className="animate-spin" size={14} aria-hidden="true" /> : <GitBranch size={14} aria-hidden="true" />}
              Lưu quan hệ
            </button>
            {relationError ? <p className="mt-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs font-normal text-red-700">{relationError}</p> : null}
          </div>
            </>
          ) : null}

          {rightTab === "note" ? (
            <>
          <div className="mt-4 flex items-center gap-2 text-xs font-normal uppercase tracking-wide text-muted">
            <StickyNote size={14} aria-hidden="true" />
            <span>Note hiện tại</span>
          </div>
          <div className="mt-2 rounded-lg border border-blue-100 bg-blue-50 px-3 py-2 text-xs leading-5 text-slate-700">
            <p className="font-normal text-ink">Markdown nhanh</p>
            <p className="mt-1">Dùng # Tiêu đề, - bullet, **đậm**, *nghiêng*, [link](https://...), &gt; quote, `code`.</p>
          </div>
          <div className="mt-2 grid gap-3 lg:grid-cols-2">
            <textarea
              className="min-h-64 w-full resize-y rounded-lg border border-line bg-slate-50 p-3 text-sm leading-6 text-ink placeholder:text-slate-400"
              value={noteDraft}
              disabled={!selectedSource}
              onChange={(event) => {
                if (!selectedSource) {
                  return;
                }

                setDrafts((current) => ({ ...current, [selectedSource.id]: event.target.value }));
              }}
              placeholder="Ghi note bằng Markdown cho mục này"
            />
            <div className="min-h-64 rounded-lg border border-line bg-white p-3">
              <div className="mb-2 flex items-center justify-between gap-2 border-b border-line pb-2">
                <span className="text-xs uppercase tracking-wide text-muted">Preview</span>
                <span className="text-xs text-muted">{noteDraft.trim().length} ký tự</span>
              </div>
              <MarkdownPreview content={noteDraft} emptyText="Preview Markdown sẽ hiện ở đây." />
            </div>
          </div>
          <div className="mt-3 rounded-lg border border-line bg-slate-50 p-3">
            <div className="mb-2 flex items-center gap-2 text-xs font-normal uppercase tracking-wide text-muted">
              <StickyNote size={14} aria-hidden="true" />
              <span>Loại note</span>
            </div>
            <div className="grid grid-cols-3 gap-2">
              {noteKindOptions.map((option) => (
                <button
                  key={option.value}
                  className={`h-9 rounded-lg border px-2 text-xs font-normal transition ${
                    noteKind === option.value
                      ? "border-blue-200 bg-blue-50 text-brand"
                      : "border-line bg-white text-slate-600 hover:bg-white"
                  }`}
                  type="button"
                  onClick={() => setNoteKind(option.value)}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>
          <button
            className="mt-3 inline-flex h-10 items-center justify-center gap-2 rounded-lg bg-brand px-4 text-sm font-normal text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:bg-slate-300"
            type="button"
            disabled={!selectedSource || !noteDraft.trim() || savingNote}
            onClick={() => void handleSaveNote()}
          >
            {savingNote ? <Loader2 className="animate-spin" size={16} aria-hidden="true" /> : <Save size={16} aria-hidden="true" />}
            Lưu note
          </button>
          {captureError ? (
            <p className="mt-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm font-normal text-red-700">{captureError}</p>
          ) : null}
            </>
          ) : null}

          {rightTab === "manage" ? (
            <>
          <div className="mt-3 rounded-lg border border-line bg-white p-3">
            <div className="flex items-center justify-between gap-2">
              <h3 className="text-xs font-normal uppercase tracking-wide text-muted">Thuộc tính note</h3>
              <button
                className="inline-flex h-8 items-center rounded-lg border border-line bg-white px-3 text-xs font-normal text-slate-700 hover:bg-slate-50"
                type="button"
                onClick={() => setNoteProperties((current) => [...current, { id: makeId(), key: "", value: "" }])}
              >
                Thêm
              </button>
            </div>
            <div className="mt-2 space-y-2">
              {noteProperties.map((property) => (
                <div key={property.id} className="grid grid-cols-[minmax(0,0.45fr)_minmax(0,0.55fr)_32px] gap-2">
                  <input
                    className="h-9 min-w-0 rounded-lg border border-line bg-slate-50 px-2 text-xs font-normal text-ink"
                    value={property.key}
                    onChange={(event) => setNoteProperties((current) => updatePair(current, property.id, "key", event.target.value))}
                    placeholder="claim"
                    aria-label="Tên thuộc tính note"
                  />
                  <input
                    className="h-9 min-w-0 rounded-lg border border-line bg-slate-50 px-2 text-xs font-normal text-ink"
                    value={property.value}
                    onChange={(event) => setNoteProperties((current) => updatePair(current, property.id, "value", event.target.value))}
                    placeholder="Giá trị"
                    aria-label="Giá trị thuộc tính note"
                  />
                  <button
                    className="inline-flex h-9 w-8 items-center justify-center rounded-lg border border-line bg-white text-xs font-normal text-red-600 hover:bg-red-50"
                    type="button"
                    aria-label="Xóa thuộc tính note"
                    onClick={() => setNoteProperties((current) => current.filter((item) => item.id !== property.id))}
                  >
                    X
                  </button>
                </div>
              ))}
            </div>
          </div>

          <div className="mt-3 rounded-lg border border-line bg-white p-3">
            <div className="flex items-center justify-between gap-2">
              <h3 className="text-xs font-normal uppercase tracking-wide text-muted">Link của note</h3>
              <button
                className="inline-flex h-8 items-center rounded-lg border border-line bg-white px-3 text-xs font-normal text-slate-700 hover:bg-slate-50"
                type="button"
                onClick={() => setNoteLinks((current) => [...current, { id: makeId(), url: "", label: "" }])}
              >
                Thêm
              </button>
            </div>
            <div className="mt-2 space-y-2">
              {noteLinks.map((link) => (
                <div key={link.id} className="grid grid-cols-[minmax(0,1fr)_minmax(0,0.55fr)_32px] gap-2">
                  <input
                    className="h-9 min-w-0 rounded-lg border border-line bg-slate-50 px-2 text-xs font-normal text-ink"
                    value={link.url}
                    onChange={(event) =>
                      setNoteLinks((current) => current.map((item) => (item.id === link.id ? { ...item, url: event.target.value } : item)))
                    }
                    placeholder="https://..."
                    aria-label="URL note"
                  />
                  <input
                    className="h-9 min-w-0 rounded-lg border border-line bg-slate-50 px-2 text-xs font-normal text-ink"
                    value={link.label}
                    onChange={(event) =>
                      setNoteLinks((current) => current.map((item) => (item.id === link.id ? { ...item, label: event.target.value } : item)))
                    }
                    placeholder="Tên link"
                    aria-label="Tên link note"
                  />
                  <button
                    className="inline-flex h-9 w-8 items-center justify-center rounded-lg border border-line bg-white text-xs font-normal text-red-600 hover:bg-red-50"
                    type="button"
                    aria-label="Xóa link note"
                    onClick={() => setNoteLinks((current) => current.filter((item) => item.id !== link.id))}
                  >
                    X
                  </button>
                </div>
              ))}
            </div>
          </div>

          <div className="mt-3 rounded-lg border border-line bg-white p-3">
            <h3 className="text-xs font-normal uppercase tracking-wide text-muted">Mối quan hệ của note</h3>
            <div className="mt-2 grid gap-2">
              <select
                className="h-9 rounded-lg border border-line bg-slate-50 px-2 text-xs font-normal text-slate-700"
                value={noteRelation.type}
                onChange={(event) => setNoteRelation((current) => ({ ...current, type: event.target.value }))}
                aria-label="Loại quan hệ note"
              >
                {relationTypeOptions.map((type) => (
                  <option key={type.id} value={type.slug}>
                    {type.slug}
                  </option>
                ))}
              </select>
              <select
                className="h-9 min-w-0 rounded-lg border border-line bg-slate-50 px-2 text-xs font-normal text-slate-700"
                value={noteRelation.targetId}
                onChange={(event) => setNoteRelation((current) => ({ ...current, targetId: event.target.value }))}
                aria-label="Đối tượng liên quan với note"
              >
                <option value="">Không tạo thêm relation</option>
                {relationTargets.map((target) => (
                  <option key={target.id} value={target.id}>
                    {target.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="mt-3 space-y-2">
              <div className="flex items-center justify-between gap-2">
                <p className="text-xs font-normal uppercase tracking-wide text-muted">Thuộc tính quan hệ</p>
                <button
                  className="inline-flex h-8 items-center rounded-lg border border-line bg-white px-3 text-xs font-normal text-slate-700 hover:bg-slate-50"
                  type="button"
                  onClick={() =>
                    setNoteRelation((current) => ({
                      ...current,
                      properties: [...current.properties, { id: makeId(), key: "", value: "" }]
                    }))
                  }
                >
                  Thêm
                </button>
              </div>
              {noteRelation.properties.map((property) => (
                <div key={property.id} className="grid grid-cols-[minmax(0,0.45fr)_minmax(0,0.55fr)_32px] gap-2">
                  <input
                    className="h-9 min-w-0 rounded-lg border border-line bg-slate-50 px-2 text-xs font-normal text-ink"
                    value={property.key}
                    onChange={(event) =>
                      setNoteRelation((current) => ({
                        ...current,
                        properties: updatePair(current.properties, property.id, "key", event.target.value)
                      }))
                    }
                    placeholder="reason"
                    aria-label="Tên thuộc tính quan hệ note"
                  />
                  <input
                    className="h-9 min-w-0 rounded-lg border border-line bg-slate-50 px-2 text-xs font-normal text-ink"
                    value={property.value}
                    onChange={(event) =>
                      setNoteRelation((current) => ({
                        ...current,
                        properties: updatePair(current.properties, property.id, "value", event.target.value)
                      }))
                    }
                    placeholder="Giá trị"
                    aria-label="Giá trị thuộc tính quan hệ note"
                  />
                  <button
                    className="inline-flex h-9 w-8 items-center justify-center rounded-lg border border-line bg-white text-xs font-normal text-red-600 hover:bg-red-50"
                    type="button"
                    aria-label="Xóa thuộc tính quan hệ note"
                    onClick={() =>
                      setNoteRelation((current) => ({
                        ...current,
                        properties: current.properties.filter((item) => item.id !== property.id)
                      }))
                    }
                  >
                    X
                  </button>
                </div>
              ))}
            </div>
          </div>

            </>
          ) : null}

          {rightTab === "history" ? (
            <>
          <div className="flex items-center justify-between gap-3">
            <div className="flex min-w-0 items-center gap-2">
              <History size={15} className="shrink-0 text-muted" aria-hidden="true" />
              <h3 className="truncate text-sm font-normal">Lịch sử note của sách</h3>
            </div>
            <span className="rounded-md border border-line bg-slate-50 px-2 py-1 text-xs font-normal text-muted">{bookNotes.length}</span>
          </div>
          <div className="mt-3 space-y-3">
            {bookNotes.length === 0 ? (
              <p className="rounded-lg border border-dashed border-line bg-slate-50 px-3 py-4 text-sm font-normal text-muted">
                Chưa có note nào trong cây sách này.
              </p>
            ) : (
              bookNotes.map((note) => (
                <article key={note.id} className="rounded-lg border border-line bg-white p-3">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="truncate text-xs font-normal text-muted">
                        {sourceById.get(note.sourceId)?.title ? `${sourceById.get(note.sourceId)?.title} · ` : ""}
                        Tạo lúc {note.createdAt}
                      </p>
                    </div>
                    <div className="flex shrink-0 flex-col items-end gap-1.5">
                      <StatusBadge kind={note.kind} />
                      <LifecycleBadge kind={note.kind} status={note.status} />
                      {onUpdateNote ? (
                        <button
                          className="inline-flex h-7 items-center gap-1 rounded-lg border border-line bg-white px-2 text-xs font-normal text-slate-700 hover:bg-slate-50"
                          type="button"
                          onClick={() => setEditingNoteId((current) => (current === note.id ? "" : note.id))}
                        >
                          <Pencil size={12} aria-hidden="true" />
                          Edit
                        </button>
                      ) : null}
                    </div>
                  </div>
                  {editingNoteId === note.id && onUpdateNote ? (
                    <div className="mt-3">
                      <NoteEditForm note={note} onSave={onUpdateNote} onCancel={() => setEditingNoteId("")} />
                    </div>
                  ) : (
                    <div className="mt-2 line-clamp-5">
                      <MarkdownPreview content={note.content} compact />
                    </div>
                  )}
                </article>
              ))
            )}
          </div>
            </>
          ) : null}
        </div>
      </aside>
    </div>
  );
}
